import type { PrototypeState } from "./controllerState";
import { byId, markSkeletonsVisible } from "./domUtils";
import type { Root } from "./prototypeTypes";
import { getPlanEntitlements } from "./planStore";

export function refreshHomeState(root: Root, state: PrototypeState) {
  const home = byId(root, "view-home");
  if (!home) return;
  home.dataset.auth = state.isLoggedIn ? "member" : "guest";
  home.dataset.plan = state.effectiveAccessPlan;
  refreshAiCreateAccess(root, state);
  refreshHomeLayout(root, home, state);
  // Skeletons are now on-screen — start the minimum-display timer so
  // fast API responses cannot flicker them away in <400ms.
  markSkeletonsVisible();
  settleMetricCounters(home);
  // Avatar dropdown is intentionally NOT painted here. The avatar is a
  // shell concern owned by avatarView.renderAvatarDropdown, called on
  // (a) dropdown open and (b) auth state transitions. Coupling home
  // refresh to the avatar caused the recurring "blank popup" bug
  // because home paints fired in different orders relative to the
  // dropdown's own renders. See avatarView.ts header.
  // Data hydration is owned by prototypeHydration/home hydrators.
  // This controller only syncs already-resolved auth/plan state into DOM.
  if (!state.isLoggedIn) {
    const card = byId(root, "resumeCard");
    if (card) card.classList.add("hidden");
  }
  const badge = byId(root, "homePlanBadge");
  const headline = byId(root, "homePlanHeadline");
  const copy = byId(root, "homePlanCopy");
  const meta = byId(root, "homePlanMeta");
  const quota = byId(root, "homeAiQuota");
	const recommendedHeading = byId(root, 'homeRecommendedHeading');
	if (recommendedHeading) recommendedHeading.textContent = state.isLoggedIn ? 'For You' : 'Explore more topics';
  if (!badge || !headline || !copy || !meta || !quota) return;

  if (state.effectiveAccessPlan === "max") {
    badge.textContent = "Max";
    // 配额数字只取已由 D1 套餐目录填充的 planStore。
    const maxQuota = getPlanEntitlements("max").privateChaptersPerMonth;
    headline.textContent = `${maxQuota} private AI chapters available each month`;
    copy.textContent = "Use Home as your learning desk: continue public topics, track progress, and generate private topics for niche problems.";
    setPlanMeta(meta, "Plan: Max", "AI Create: unlocked");
    quota.textContent = `Quota: ${maxQuota} chapters / month`;
  } else if (state.effectiveAccessPlan === "pro") {
    badge.textContent = "Pro";
    const proQuota = getPlanEntitlements("pro").privateChaptersPerMonth;
    headline.textContent = `${proQuota} private AI chapters available each month`;
    copy.textContent = "Pro keeps AI Create one click away while Home stays focused on progress, recommendations, and the next useful lesson.";
    setPlanMeta(meta, "Plan: Pro", "AI Create: unlocked");
    quota.textContent = `Quota: ${getPlanEntitlements("pro").privateChaptersPerMonth} chapters / month`;
  } else if (state.effectiveAccessPlan === "lite") {
    badge.textContent = "Lite";
    headline.textContent = "Unlimited public topics across every mode";
    copy.textContent = "Lite is built for consuming the curated library: full chapters, audio, video, and community participation without private generation.";
    setPlanMeta(meta, "Plan: Lite", "AI Create: locked");
    quota.textContent = `Quota: ${getPlanEntitlements(state.effectiveAccessPlan).privateChaptersPerMonth} chapters / month`;
  } else {
    badge.textContent = "Free";
    // P2.6: "3" = FREE_CHAPTER_COUNT (per-topic preview window), not a monthly quota.
    // The same number is surfaced on the avatar badge, the paywall feature
    // lists, and the per-topic chapter policy. Keep them in sync so the
    // free user does not see two different "3"s.
    // Source of truth: src/domain/access/publicChapterPolicy.ts FREE_CHAPTER_COUNT
    headline.textContent = "First 3 chapters of every topic are free";
    copy.textContent = "Use free chapters to test the structure, then upgrade when you want every public topic or private AI-generated topics.";
    setPlanMeta(meta, "Plan: Free", "AI Create: locked");
    quota.textContent = `Quota: ${getPlanEntitlements(state.effectiveAccessPlan).privateChaptersPerMonth} chapters / month`;
  }
}

function refreshAiCreateAccess(root: Root, state: PrototypeState) {
  // All users (logged-in or not) see the AI Create page structure.
  // Generate button gating is handled at showAiCreateModal click guard.
  const unlocked = true;
  const gate = byId(root, "aiCreateGate");
  const body = byId(root, "aiCreateUnlocked");
  if (gate) gate.style.display = "none";
  if (body) body.style.display = "";
}

function setPlanMeta(meta: HTMLElement, ...items: string[]) {
  meta.replaceChildren(
    ...items.map((item) => {
      const span = document.createElement("span");
      span.textContent = item;
      return span;
    }),
  );
}

function refreshHomeLayout(root: Root, home: HTMLElement, state: PrototypeState) {
  // 所有登录用户 (free/lite/pro/max) 共用同一套 member 布局，
  // 只有付费墙、权限和营销元素的区别。访客走 guest 布局。
  // 之前 free 用户和 guest 共用布局是不对的——free 也是登录用户，
  // 应该看到 Learning desk、最近学习等 member 功能区。
  const visibleById: Record<string, boolean> = {
    homeAuthSkeleton: false,
    guestHeroSection: !state.isLoggedIn,
    memberWorkspaceSection: state.isLoggedIn,
		// Recently opened and Saved are member-only sections. Their loaders retain
		// ownership of the resolved hidden state, so an empty rail has no header.
		recentSection: state.isLoggedIn,
		savedSection: state.isLoggedIn,
		mobileHomeLibrarySection: state.isLoggedIn,
    // 访客 CTA: 仅在未登录时显示, 取代隐掉的 recent header
    // 死区。logged-in 时隐藏 (否则会与 recent content 同时
    // 出现, 视觉冲突)。这是 round-6 营销钩子, 把 visitorGuard
    // 的拦截信号转成主动留资 CTA, 直接挂 showRegisterModal()
    // 跳到注册 tab (旧路径是拦截 -> login -> 切 register, 多 1 步)。
    visitorRecentCta: !state.isLoggedIn,
		homeVisitorCatalogCta: !state.isLoggedIn,
    featuredSection: true,
    trendingSection: true,
		newestSection: true,
		mobileHomeDiscoverySection: state.isLoggedIn,
		multimodalSection: !state.isLoggedIn,
		communitySection: !state.isLoggedIn,
  };
  for (const [id, visible] of Object.entries(visibleById)) {
    const node = byId(root, id);
    if (node) node.style.display = visible ? "" : "none";
  }
  const order = state.isLoggedIn
	? ["homeAuthSkeleton", "memberWorkspaceSection", "mobileHomeLibrarySection", "recentSection", "savedSection", "trendingSection", "newestSection", "mobileHomeDiscoverySection", "featuredSection"]
    : [
        "homeAuthSkeleton",
        "guestHeroSection",
        "trendingSection",
        "multimodalSection",
		"newestSection",
		"visitorRecentCta",
        "communitySection",
		"featuredSection",
      ];
  for (const id of order) {
    const node = byId(root, id);
    if (node) home.appendChild(node);
  }
}

function settleMetricCounters(scope: Pick<Document, "querySelectorAll">) {
  const nodes = scope.querySelectorAll<HTMLElement>(".rolling-number");
  // Two-pass to avoid layout thrashing: batch all getComputedStyle reads first,
  // then perform all textContent writes. Interleaving read-write per node forces
  // the browser to flush layout N times instead of once.
  const updates: { node: HTMLElement; formatted: string | null }[] = [];
  nodes.forEach((node) => {
    const parent = node.closest<HTMLElement>(".home-metric, .paid-hero-metric");
    if (parent && getComputedStyle(parent).display === "none") return;
    const value = Number.parseFloat(node.dataset.value ?? "0");
    const decimals = Number.parseInt(node.dataset.decimals ?? "0", 10);
    const formatted = Number.isFinite(value) ? formatRollingMetric(value, decimals, node.dataset.format, node.dataset.suffix) : node.textContent;
    updates.push({ node, formatted: formatted ?? "" });
  });
  for (const { node, formatted } of updates) {
    node.textContent = formatted;
  }
}

function formatRollingMetric(value: number, decimals: number, format?: string, suffix?: string) {
  let text = decimals > 0 ? value.toFixed(decimals) : Math.round(value).toString();
  if (format === "comma") {
    const parts = text.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    text = parts.join(".");
  }
  return text + (suffix || "");
}
